import { useState, useEffect, useRef } from "react";
import { FiClient } from "../sdk";
import type { ExchangeClientConfig } from "../sdk/types/client";

export interface UseFiClientConfig {
  baseURL: string;
  mnemonic?: string;
  chainID?: number;
  maxRetries?: number;
}

export interface UseFiClientResult {
  /** Initialized FiClient when baseURL and mnemonic are set and init succeeded; null otherwise */
  client: FiClient | null;
  /** True when client is created and initialize() has resolved successfully */
  isReady: boolean;
  /** Last initialization error, if any */
  error: Error | null;
}

/**
 * Creates and initializes FiClient from baseURL and mnemonic.
 * Cleans up when dependencies change or mnemonic is cleared.
 */
export function useFiClient(config: UseFiClientConfig): UseFiClientResult {
  const { baseURL, mnemonic, chainID, maxRetries } = config;
  const [client, setClient] = useState<FiClient | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const initRef = useRef(0);

  useEffect(() => {
    if (!baseURL || !mnemonic || mnemonic.trim() === "") {
      setClient(null);
      setIsReady(false);
      setError(null);
      return;
    }

    const buildConfig: ExchangeClientConfig = {
      baseURL,
      mnemonic: mnemonic.trim(),
    };
    if (chainID !== undefined) buildConfig.chainID = chainID;
    if (maxRetries !== undefined) buildConfig.maxRetries = maxRetries;

    const instance = new FiClient(buildConfig);
    const gen = ++initRef.current;
    setClient(null);
    setIsReady(false);
    setError(null);

    instance
      .initialize()
      .then(() => {
        if (gen === initRef.current) {
          setClient(instance);
          setIsReady(true);
          setError(null);
        }
      })
      .catch((err) => {
        if (gen === initRef.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setClient(null);
          setIsReady(false);
        }
      });

    return () => {
      initRef.current = gen + 1;
    };
  }, [baseURL, mnemonic, chainID, maxRetries]);

  return { client, isReady, error };
}
